import React from "react";
// UI Framework
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// Common Components
import { Button, Modal } from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
// Local Components
import AddGroupModal from "./AddGroupModal";
import AddLimitItemModal from "./AddLimitItemModal";
import AddSymbolModal from "./AddSymbolModal";
import LimitsModal from "./LimitsModal";
import { getAssetLimitAdditional } from "./AssetLimitAdditional";
import { getLimitItemAdditional } from "./LimitItemAdditional";
// Types
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  IAssetLimitSearchParams,
  IAssetLimitTableReport,
  ILimitItemSearchParams,
  ILimitItemTableReport,
} from "src/Interfaces/portfolioGroups";
import {
  getPortfoGroupAssetLimit,
  getPortfoGroupLimitItem,
  getPortfolioGroupTabs,
} from "src/Requests/portfolioGroups";
import { packetErrorHandler } from "src/Utils/requestUtils";

enum ModalMode {
  AddGroup = 1,
  AddSymbol = 2,
  Limits = 3,
  AddLimitItem = 4,
  Closed = 5,
}

const PortfolioGroups = () => {
  const prevRowRef = React.useRef<number | null>(null);
  const [modal, setModal] = React.useState<ModalMode>(ModalMode.Closed);
  const [portfolioGroups, setPortfolioGroups] = React.useState<DropdownItem[]>(
    [],
  );
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IAssetLimitSearchParams>({} as IAssetLimitSearchParams);
  const [limitItemSearchParams, setLimitItemSearchParams] =
    React.useState<ILimitItemSearchParams | null>(null);
  const [selectedAssetLimitRow, setSelectedAssetLimitRow] =
    React.useState<IAssetLimitTableReport | null>(null);
  const [limitItemRefreshFlag, setLimitItemRefreshFlag] = React.useState(false);
  const [refreshFlag, setrRefreshFlag] = React.useState(false);

  React.useEffect(() => {
    if (limitItemRefreshFlag) setLimitItemRefreshFlag(false);
  }, [limitItemRefreshFlag]);

  const loadPortfolioGroups = React.useCallback(() => {
    getPortfolioGroupTabs()
      .then((resp) => {
        setPortfolioGroups(resp);
        if (resp.length && !serverSearchParams.portfolioGroupId) {
          setServerSearchParams((prev) => ({
            ...prev,
            portfolioGroupId: Number(resp[0].id),
          }));
        }
      })
      .catch(packetErrorHandler);
  }, [serverSearchParams.portfolioGroupId]);

  const getModalUI = React.useMemo(() => {
    switch (modal) {
      case ModalMode.AddGroup:
        return (
          <AddGroupModal
            onSuccess={() => {
              loadPortfolioGroups();
              setrRefreshFlag(true);
              setLimitItemRefreshFlag(true);
              setModal(ModalMode.Closed);
            }}
          />
        );
      case ModalMode.AddSymbol:
        return (
          <AddSymbolModal
            onSuccess={() => {
              setrRefreshFlag(true);
              setLimitItemRefreshFlag(true);
            }}
            onClose={() => setModal(ModalMode.Closed)}
          />
        );
      case ModalMode.Limits:
        return <LimitsModal />;
      case ModalMode.AddLimitItem:
        return (
          <AddLimitItemModal
            portfolioGroupId={selectedAssetLimitRow?.portfolioGroupId}
            assetType={selectedAssetLimitRow?.assetType}
            onSuccess={() => {
              setrRefreshFlag(true);
              setLimitItemRefreshFlag(true);
              setModal(ModalMode.Closed);
            }}
          />
        );
      case ModalMode.Closed:
        return null;
    }
  }, [modal, selectedAssetLimitRow, loadPortfolioGroups]);

  React.useEffect(() => {
    loadPortfolioGroups();
  }, [loadPortfolioGroups]);

  React.useEffect(() => {
    prevRowRef.current = null;
    setSelectedAssetLimitRow(null);
    setLimitItemSearchParams(null);
  }, [serverSearchParams.portfolioGroupId]);

  const portfolioGroupChangeHandler = (name: string, value: any) => {
    prevRowRef.current = null;
    setSelectedAssetLimitRow(null);
    setLimitItemSearchParams(null);
    setLimitItemRefreshFlag(true);
    setServerSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const canAddLimitItem = Boolean(
    selectedAssetLimitRow?.portfolioGroupId && selectedAssetLimitRow?.assetType,
  );

  const limitItemAdditional = React.useMemo(
    () =>
      limitItemSearchParams
        ? getLimitItemAdditional(limitItemSearchParams.assetType)
        : null,
    [limitItemSearchParams?.assetType],
  );

  const assetLimitAdditional = React.useMemo(
    () => getAssetLimitAdditional(() => setLimitItemRefreshFlag(true)),
    [],
  );

  const assetLimitRowClickHandler = (row: IAssetLimitTableReport) => {
    if (prevRowRef.current === row.id) return;
    prevRowRef.current = row.id;
    setSelectedAssetLimitRow(row);
    if (row.assetType != null && row.portfolioGroupId != null) {
      setLimitItemSearchParams({
        assetType: row.assetType,
        portfolioGroupId: row.portfolioGroupId,
      });
    }
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      {modal !== ModalMode.Closed && (
        <Modal
          title={modal === ModalMode.AddGroup ? "ایجاد/حذف گروه پورتفو" : modal === ModalMode.AddSymbol ? "اضافه کردن گروهی نماد" : modal === ModalMode.Limits ? "مشاهده محدودیت‌ها" : "افزودن محدودیت"}
          onClose={() => setModal(ModalMode.Closed)}
          UI={getModalUI}
          modalBoxClassName={
            modal === ModalMode.AddSymbol || modal === ModalMode.AddLimitItem
              ? "!min-w-min !min-h-min"
              : ""
          }
        />
      )}
      <div className="flex w-full justify-between">
        <div className="w-2/3">
          <SelectableButtons
            label=""
            name="portfolioGroupId"
            onChange={portfolioGroupChangeHandler}
            options={portfolioGroups}
            value={serverSearchParams?.portfolioGroupId?.toString() ?? ""}
            hideSelectAll
          />
        </div>
        <div className="flex gap-2 w-1/3">
          <Button
            text="ایجاد/حذف گروه پورتفو"
            onClick={() => setModal(ModalMode.AddGroup)}
            variant="outlined"
          />
          <Button
            text="اضافه کردن گروهی نماد"
            onClick={() => setModal(ModalMode.AddSymbol)}
            variant="outlined"
          />
          <Button
            text={
              <span className="inline-flex items-center gap-1">
                <InfoOutlinedIcon fontSize="small" />
                <span>مشاهده محدودیت ها</span>
              </span>
            }
            onClick={() => setModal(ModalMode.Limits)}
            variant="outlined"
          />
        </div>
      </div>
      <div className="flex flex-row justify-start items-start gap-3 mt-3">
        <div className="flex flex-col flex-1">
          <div className="min-h-8 mb-2" />

          <TableTemplate<IAssetLimitSearchParams, IAssetLimitTableReport>
            TableUI={({ reports, onAction, sortingType, setSortingType }) => (
              <CommonTableUI
                reports={reports}
                onAction={onAction}
                AdditionalBody={assetLimitAdditional}
                sortingType={sortingType}
                setSortingType={setSortingType}
                onTableRowClick={assetLimitRowClickHandler}
                activeRowId={selectedAssetLimitRow?.id}
                tableBoxClassName="max-h-[500px]"
                ref={prevRowRef}
                refreshFlag={refreshFlag}
              />
            )}
            refreshTableTime={30000}
            getDataTable={getPortfoGroupAssetLimit}
            searchParameters={serverSearchParams}
            withSort
            tableWrapperClassname="!mx-0"
          />
        </div>
        <div className="flex flex-col mt-4 flex-1">
          <div className="min-h-8 flex items-center justify-between gap-2 mb-2">
            <span className="text-sm font-bold mr-6">
              طبقه دارایی / استراتژی: {selectedAssetLimitRow?.assetTypeName ?? "-"}
            </span>
            <div
              className={`rounded-full flex justify-center items-center !w-11 !h-11 ${
                canAddLimitItem
                  ? "bg-sky-600 cursor-pointer"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
              onClick={() =>
                canAddLimitItem ? setModal(ModalMode.AddLimitItem) : undefined
              }
            >
              <AddIcon className="text-white" />
            </div>
          </div>
          {limitItemSearchParams ? (
            <TableTemplate<ILimitItemSearchParams, ILimitItemTableReport>
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  AdditionalBody={limitItemAdditional}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  tableBoxClassName="max-h-[500px]"
                  refreshFlag={limitItemRefreshFlag}
                />
              )}
              refreshTableTime={30000}
              getDataTable={getPortfoGroupLimitItem}
              searchParameters={limitItemSearchParams}
              withSort
              tableWrapperClassname="!mx-0"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PortfolioGroups;
